<template>
  <div class="flex-1 min-h-0 flex flex-col overflow-hidden text-gray-800">
    <div
      ref="scrollRef"
      class="relative flex flex-1 min-h-0 flex-col overflow-y-auto px-4 tablet-h:px-[14px] pt-[calc(env(safe-area-inset-top)+68px)] pb-[calc(env(safe-area-inset-bottom)+100px)] tablet-h:py-[30px]"
      @scroll="handleScroll"
    >
      <div class="w-full max-w-[1342px] mx-auto">
        <div class="mb-[26px]">
          <LinkBreadcrumb
            :items="breadcrumbItems"
            @select="handleBreadcrumbSelect"
          />
        </div>

        <template v-if="report">
          <!-- Mobile: full-width title, then the two-line stamp beside the
               actions. tablet-h+: the actions sit right of title + stamp. -->
          <div
            class="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2.5 mb-[27px] tablet-h:mb-[30px]"
          >
            <div
              class="col-span-2 tablet-h:col-span-1 font-medium text-[24px] leading-[30px] tablet-h:text-[22px] tablet-h:leading-[28px] mb-4 tablet-h:mb-2"
            >
              {{ title }}
            </div>

            <div class="text-[14px] leading-[18px]">
              Created<br class="tablet-h:hidden" />
              {{ formatTimestamp(report.timestamp) }}
            </div>

            <div
              v-if="canUseOwnerActions || pdfDownloadUrl"
              class="flex items-center gap-x-2.5 tablet-h:col-start-2 tablet-h:row-start-1 tablet-h:row-span-2"
            >
              <!-- One stroked segment: up | divider | down. -->
              <div
                v-if="canUseOwnerActions"
                class="flex items-center h-[38px] tablet-h:h-[30px] rounded-[7px] border border-gray-200 hover:border-gray-300 transition"
              >
                <button
                  type="button"
                  aria-label="Report was helpful"
                  :aria-pressed="feedbackState === FeedbackRating.Up"
                  class="flex items-center h-full pl-[11px] pr-2.5 cursor-pointer"
                  @click="handleThumbsUp"
                >
                  <ThumbsUpIcon
                    class="size-[18px] tablet-h:size-4"
                    :class="
                      feedbackState === FeedbackRating.Up
                        ? 'text-green-600 hover:text-green-700'
                        : 'text-gray-400 hover:text-gray-600'
                    "
                  />
                </button>
                <div class="w-px h-5 tablet-h:h-4 bg-gray-200" />
                <button
                  type="button"
                  aria-label="Report was not helpful"
                  :aria-pressed="feedbackState === FeedbackRating.Down"
                  class="flex items-center h-full pl-2.5 pr-[11px] cursor-pointer"
                  @click="handleThumbsDown"
                >
                  <ThumbsUpIcon
                    class="size-[18px] tablet-h:size-4 rotate-180"
                    :class="
                      feedbackState === FeedbackRating.Down
                        ? 'text-red-600 hover:text-red-700'
                        : 'text-gray-400 hover:text-gray-600'
                    "
                  />
                </button>
              </div>
              <!-- Square stroke button: Button's icon-only box is wider with a
                   larger glyph than Sketch's. -->
              <button
                v-if="pdfDownloadUrl"
                type="button"
                aria-label="Download report"
                class="flex items-center justify-center size-[38px] tablet-h:size-[30px] rounded-[7px] tablet-h:rounded-[6px] border border-gray-200 text-gray-800 hover:border-gray-300 transition cursor-pointer"
                @click="handleDownload"
              >
                <DownloadIcon class="size-[18px] tablet-h:size-4" />
              </button>
              <Button
                v-if="canUseOwnerActions"
                :size="isDesktopLayout ? 'md' : 'lg'"
                :icon="ShareIcon"
                @click="handleShare"
              >
                Share
              </Button>
            </div>
          </div>

          <Card.Base class="mb-[60px]">
            <Card.Title :icon="InfoIcon">
              Technical analysis overview
            </Card.Title>

            <Card.Content>
              <div
                class="grid grid-cols-2 tablet-h:grid-cols-3 gap-3 tablet-h:gap-x-5 tablet-h:gap-y-[25px]"
              >
                <div
                  class="bg-gray-200/20 rounded-xl px-3 pb-4 tablet-h:p-5 pt-[14px]!"
                >
                  <div
                    class="flex items-start tablet-h:items-center gap-1.5 min-h-9 tablet-h:min-h-0 mb-3 tablet-h:mb-[18px]"
                  >
                    <div
                      class="font-medium text-[14px] leading-[18px] tablet-h:leading-[22px] text-gray-900"
                    >
                      {{
                        isAudioReport
                          ? 'Regular match percentage'
                          : 'Global match percentage'
                      }}
                    </div>
                    <Tooltip
                      size="lg"
                      class="max-w-[309px]!"
                      content="A weighted score reflecting both the severity of detected matches and how much of the registered work was found to match."
                    >
                      <InfoSmallIcon class="size-3 text-gray-400" />
                    </Tooltip>
                  </div>

                  <div
                    class="w-full max-w-[120px] tablet-h:max-w-[170px] aspect-square relative flex items-center justify-center mx-auto"
                  >
                    <svg
                      v-if="!coverOnly"
                      class="size-full absolute -rotate-90"
                      :viewBox="`0 0 ${radius * 2} ${radius * 2}`"
                      :class="{
                        'stroke-red-500': report?.level === ScoreLevel.High,
                        'stroke-yellow-500':
                          report?.level === ScoreLevel.Medium,
                        'stroke-green-500': report?.level === ScoreLevel.Low,
                      }"
                    >
                      <circle
                        class="fill-transparent stroke-16 tablet-h:stroke-20 stroke-[#D8D8D8]"
                        :r="actualRadius"
                        :cx="radius"
                        :cy="radius"
                      />
                      <circle
                        class="fill-transparent stroke-16 tablet-h:stroke-20"
                        :r="actualRadius"
                        :cx="radius"
                        :cy="radius"
                        :stroke-dasharray="`${circumference} ${circumference}`"
                        :stroke-dashoffset="strokeDashoffset"
                      />
                    </svg>

                    <div
                      class="font-medium text-center"
                      :class="{
                        'text-red-500': report?.level === ScoreLevel.High,
                        'text-yellow-500': report?.level === ScoreLevel.Medium,
                        'text-green-500': report?.level === ScoreLevel.Low,
                      }"
                    >
                      <div
                        class="text-[22px] tablet-h:text-[34px] leading-[28px] tablet-h:leading-[42px]"
                      >
                        {{
                          coverOnly
                            ? 'Accepted'
                            : `${formatNumber(report?.score ?? 0)}%`
                        }}
                      </div>

                      <div
                        class="text-[11px] tablet-h:text-[18px] leading-[13px] tablet-h:leading-[20px]"
                      >
                        {{
                          coverOnly
                            ? 'cover match'
                            : isAudioReport
                              ? 'regular match'
                              : 'match'
                        }}
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  class="bg-gray-200/20 rounded-xl px-3 pb-4 tablet-h:p-5 pt-[14px]!"
                >
                  <div
                    class="flex items-start tablet-h:items-center gap-1.5 min-h-9 tablet-h:min-h-0 mb-[5px] tablet-h:mb-2"
                  >
                    <div
                      class="font-medium text-[14px] leading-[18px] tablet-h:leading-[22px] text-gray-900"
                    >
                      {{
                        isAudioReport
                          ? 'Regular analysis matches'
                          : 'Potential infringements'
                      }}
                    </div>
                    <Tooltip
                      size="lg"
                      class="max-w-[271px]!"
                      content="Total potential infringement matches found between these two works."
                    >
                      <InfoSmallIcon class="size-3 text-gray-400" />
                    </Tooltip>
                  </div>

                  <div
                    class="text-[40px] leading-[40px] tablet-h:leading-[50px] font-medium text-gray-900 mb-2"
                  >
                    {{ formatNumber(report?.matches.total ?? 0) }}
                    <span class="hidden tablet-h:inline-block">matches</span>
                  </div>

                  <div class="flex flex-col gap-y-[3px]">
                    <div
                      v-for="section in displaySections"
                      :key="section.key"
                      class="flex items-center gap-x-2"
                      @click="handleCategoryClick(section.key)"
                    >
                      <div
                        class="hidden tablet-h:flex size-4 items-center justify-center"
                      >
                        <component
                          :is="section.icon"
                          class="size-4 text-gray-800"
                        />
                      </div>
                      <Link icon="arrow">
                        {{ formatNumber(section.total) }}
                        {{ section.label }}
                        {{ pluralize('match', section.total) }}
                      </Link>
                    </div>
                  </div>
                </div>

                <div
                  class="col-span-full tablet-h:col-span-1 bg-gray-200/20 rounded-xl p-4 tablet-h:p-5 pt-[14px]!"
                >
                  <div
                    class="font-medium text-[14px] leading-[22px] text-gray-900 mb-[5px] tablet-h:mb-2"
                  >
                    Comparing
                  </div>
                  <div class="flex flex-col gap-y-1">
                    <div class="flex items-center gap-x-2.5">
                      <div
                        class="min-w-[70px] tablet-h:min-w-[80px] max-w-[70px] tablet-h:max-w-[80px] h-[70px] tablet-h:h-20 bg-gray-200/25 rounded-[3px] p-[7px] shrink-0"
                      >
                        <Thumbnail
                          :type="fileTypeToThumbnail(report?.copyright.type)"
                          :src="copyrightPreviewImageUrl"
                        />
                      </div>
                      <div class="w-full flex flex-col gap-y-1">
                        <div class="text-[12px] leading-[16px] text-gray-500">
                          Your registered work
                        </div>
                        <div
                          v-if="report?.copyright.name"
                          v-truncate
                          class="w-full text-[14px] leading-[18px]"
                        >
                          <div data-truncate-target>
                            {{ report.copyright.name }}
                          </div>
                        </div>
                        <RouterLink
                          :to="`/file/${report?.copyright.copyright.id}`"
                          class="text-[14px] leading-[18px] text-link"
                        >
                          View registration
                        </RouterLink>
                      </div>
                    </div>

                    <div class="w-[70px] tablet-h:w-20 flex justify-center">
                      <SortIcon class="size-[18px] text-gray-800" />
                    </div>

                    <div class="flex items-center gap-x-2.5">
                      <div
                        class="min-w-[70px] tablet-h:min-w-[80px] max-w-[70px] tablet-h:max-w-[80px] h-[70px] tablet-h:h-20 bg-gray-200/25 rounded-[3px] p-[7px] shrink-0"
                      >
                        <Thumbnail
                          :type="
                            fileTypeToThumbnail(report?.infringingFile.type)
                          "
                          :src="report?.infringingFile.asset.previewImageUrl"
                        />
                      </div>
                      <div class="w-full flex flex-col gap-y-1">
                        <div class="text-[12px] leading-[16px] text-gray-500">
                          Potential infringing work
                        </div>
                        <div v-if="report?.infringingFile.name" v-truncate>
                          <div
                            data-truncate-target
                            class="text-[14px] leading-[18px]"
                          >
                            {{ report?.infringingFile.name }}
                          </div>
                        </div>
                        <RouterLink
                          v-if="report?.infringingFile.copyrightId"
                          :to="`/file/${report.infringingFile.copyrightId}`"
                          class="text-[14px] leading-[18px] text-link"
                        >
                          View registration
                        </RouterLink>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <AudioMethodEvidence
                v-if="isAudioReport"
                :bundle="report.audioBundle"
                class="mt-5"
              />

              <Card.Separator />

              <div class="font-medium text-[14px] leading-[22px] mb-[15px]">
                Infringment findings
              </div>
              <div class="text-[14px] leading-[20px]">
                {{ report?.summary }}
              </div>

              <Card.Separator />

              <div class="font-medium text-[14px] leading-[22px] mb-[25px]">
                Potential infringing work data
              </div>

              <Attributes
                v-if="report?.infringingFile"
                :attributes="report.infringingFile.attributes"
              />

              <Card.Separator />

              <div v-if="report?.infringingFile" v-truncate>
                <div
                  class="font-medium text-[14px] leading-[22px]"
                  data-truncate-target
                >
                  File preview: {{ report?.infringingFile.name }}
                </div>
              </div>

              <div
                v-if="infringingTotalPages"
                class="text-[12px] leading-[16px] text-gray-500 mb-3"
              >
                Pages: {{ formatNumber(infringingTotalPages) }}
              </div>

              <div
                class="w-full h-[224px] bg-[#FAFAFA] rounded-xl p-[9px] tablet-v:h-[414px] relative overflow-hidden"
              >
                <Player
                  v-if="canPlayReportMedia && infringingStreamUrl"
                  :url="infringingStreamUrl"
                  :type="report!.infringingFile.type"
                  :poster="infringingPosterUrl"
                  class="absolute! inset-0 p-[9px]"
                />
                <Transition v-else name="fade" mode="out-in">
                  <img
                    v-if="showInfringingPreviewImage"
                    :src="infringingPreviewImageUrl"
                    :alt="report!.infringingFile.name"
                    class="size-full rounded-[10px] object-contain"
                  />
                  <div
                    v-else
                    class="absolute left-1/2 top-1/2 size-[170px] -translate-x-1/2 -translate-y-1/2 tablet-v:size-[280px] tablet-h:size-[320px]"
                  >
                    <Thumbnail
                      :type="fileTypeToThumbnail(report?.infringingFile.type)"
                      :alt="report?.infringingFile.name"
                    />
                  </div>
                </Transition>
              </div>

              <div v-if="report?.timeline && timelineData" ref="timelineRef">
                <TimelineCompare
                  :infringing="timelineData.infringing"
                  :registered="timelineData.registered"
                  :matches="timelineMatches"
                  class="mt-[25px]"
                  @view="handleTimelineView"
                />
              </div>
            </Card.Content>
          </Card.Base>

          <div class="flex flex-col gap-y-[60px]">
            <div
              v-for="section in displaySections"
              :key="section.key"
              :id="section.key"
            >
              <div class="flex items-center gap-x-2.5">
                <div
                  class="size-[26px] tablet-h:size-[30px] rounded-full bg-red-200/50 flex items-center justify-center tablet-h:mr-2.5 shrink-0"
                >
                  <AlertIcon class="size-[14px] tablet-h:size-4 text-red-700" />
                </div>

                <div
                  class="font-medium text-[18px] leading-[24px] tablet-h:text-[24px] tablet-h:leading-[28px]"
                >
                  {{ formatNumber(section.total) }}
                  {{ section.label }}
                  {{ pluralize('match', section.total) }}
                </div>
              </div>

              <div
                class="w-full h-px bg-gray-200/50 my-4 tablet-h:my-[25px]"
              ></div>

              <template v-if="report">
                <div
                  v-for="item in section.matches"
                  :key="item.key"
                  :id="`match-${matchScrollId(item.match)}`"
                  :ref="(el) => registerMatchEl(el, item.key)"
                  class="mb-[25px] scroll-mt-[80px]"
                >
                  <template v-if="mountedMatches.has(item.key)">
                    <Match
                      v-if="isDesktopLayout"
                      :type="item.type"
                      :match="item.match"
                      :number="item.number"
                      :allow-playback="canPlayReportMedia"
                      :report-id="report.id"
                      :can-manage="canUseOwnerActions"
                    />
                    <MobileMatch
                      v-else
                      :type="item.type"
                      :match="item.match"
                      :number="item.number"
                      :allow-playback="canPlayReportMedia"
                      :report-id="report.id"
                      :can-manage="canUseOwnerActions"
                    />
                  </template>
                  <div v-else class="h-[520px] tablet-h:h-[680px]" />
                </div>
              </template>
            </div>
          </div>
        </template>

        <template v-else>
          <div class="mb-[26px] tablet-h:mb-[30px]">
            <div
              class="font-medium text-[20px] leading-[24px] tablet-h:text-[22px] tablet-h:leading-[28px] mb-[13px] tablet-h:mb-2"
            >
              {{ title }}
            </div>
            <Skeleton class="h-[20px] w-[190px] rounded" />
          </div>

          <Card.Base class="mb-[60px]">
            <Card.Title :icon="InfoIcon">
              Technical analysis overview
            </Card.Title>

            <Card.Content>
              <div
                class="grid grid-cols-2 tablet-h:grid-cols-3 gap-3 tablet-h:gap-x-5 tablet-h:gap-y-[25px]"
              >
                <div
                  class="bg-gray-200/10 rounded-xl px-3 pb-4 tablet-h:p-5 pt-[14px]!"
                >
                  <Skeleton
                    class="h-[18px] w-[170px] max-w-full rounded mb-[18px]"
                  />
                  <Skeleton
                    class="size-[120px] tablet-h:size-[170px] mx-auto rounded-full border-16 tablet-h:border20 border-[#F6F7F7] bg-transparent!"
                  />
                </div>

                <div
                  class="bg-gray-200/10 rounded-xl px-3 pb-4 tablet-h:p-5 pt-[14px]!"
                >
                  <Skeleton
                    class="h-[18px] w-[150px] max-w-full rounded mb-[18px]"
                  />
                  <Skeleton
                    class="h-[40px] w-[160px] max-w-full rounded mb-4"
                  />
                  <div class="flex flex-col gap-y-2">
                    <Skeleton class="h-[16px] w-[130px] rounded" />
                    <Skeleton class="h-[16px] w-[110px] rounded" />
                  </div>
                </div>

                <div
                  class="col-span-full tablet-h:col-span-1 bg-gray-200/10 rounded-xl p-4 tablet-h:p-5 pt-[14px]!"
                >
                  <Skeleton class="h-[18px] w-[90px] rounded mb-[18px]" />
                  <div class="flex items-center gap-x-2.5 mb-3">
                    <Skeleton
                      class="size-[70px] tablet-h:size-20 rounded-[3px] shrink-0"
                    />
                    <div class="flex w-full flex-col gap-y-2">
                      <Skeleton class="h-[12px] w-[120px] rounded" />
                      <Skeleton class="h-[14px] w-full max-w-[220px] rounded" />
                    </div>
                  </div>
                  <div class="flex items-center gap-x-2.5">
                    <Skeleton
                      class="size-[70px] tablet-h:size-20 rounded-[3px] shrink-0"
                    />
                    <div class="flex w-full flex-col gap-y-2">
                      <Skeleton class="h-[12px] w-[130px] rounded" />
                      <Skeleton class="h-[14px] w-full max-w-[240px] rounded" />
                    </div>
                  </div>
                </div>
              </div>

              <Card.Separator />

              <div class="font-medium text-[14px] leading-[22px] mb-[15px]">
                Infringment findings
              </div>
              <div class="flex flex-col gap-y-2">
                <Skeleton class="h-[14px] w-full rounded" />
                <Skeleton class="h-[14px] w-full rounded" />
                <Skeleton class="h-[14px] w-full rounded" />
                <Skeleton class="h-[14px] w-2/3 rounded" />
              </div>

              <Card.Separator />

              <div class="font-medium text-[14px] leading-[22px] mb-[25px]">
                Potential infringing work data
              </div>
              <div
                class="grid grid-cols-2 tablet-h:grid-cols-4 gap-x-5 gap-y-4"
              >
                <div v-for="n in 4" :key="n" class="flex flex-col gap-y-2">
                  <Skeleton class="h-[12px] w-[90px] rounded" />
                  <Skeleton class="h-[14px] w-[70px] rounded" />
                </div>
              </div>

              <Card.Separator />

              <Skeleton class="h-[16px] w-[240px] rounded mb-3" />
              <Skeleton
                class="h-[224px] tablet-v:h-[414px] w-full rounded-xl"
              />
            </Card.Content>
          </Card.Base>
        </template>
      </div>
    </div>

    <BackPill
      :visible="backPill.visible"
      :label="backPill.label"
      @click="handleBackPillClick"
    />
  </div>
</template>

<script setup lang="ts">
import AudioMethodEvidence from '@/features/infringement/components/AudioMethodEvidence.vue'
import Player from '@/features/copyright/components/Player.vue'
import BackPill from '@/features/infringement/components/BackPill.vue'
import TimelineCompare from '@/features/infringement/components/TimelineCompare.vue'
import type { TimelineMatch } from '@/features/infringement/components/timeline-types'
import Attributes from '@/shared/components/Attributes.vue'
import Match from '@/features/infringement/components/Match.vue'
import MobileMatch from '@/features/infringement/components/MobileMatch.vue'
import {
  MATCH_CATEGORY_BREAKDOWN,
  MATCH_CATEGORY_PAGE_SIZE,
  useMatchCategoryMatches,
} from '@/features/infringement/composables/useMatchCategoryMatches'
import { graphql } from '@/shared/graphql/router'
import {
  ScoreLevel,
  MatchCategoryType,
  FeedbackRating,
  FeedbackType,
  ReportRole,
} from '@/shared/graphql/router/graphql'
import { useDevice } from '@/shared/composables/useDevice'
import type { ApolloCache } from '@apollo/client'
import { formatTimestamp } from '@/shared/utils/datetime'
import { formatNumber } from '@/shared/utils/number'
import { fileTypeToThumbnail } from '@/shared/utils/thumbnail'
import { applyAuthorizationToken } from '@/shared/utils/url'
import {
  Button,
  Link,
  LinkBreadcrumb,
  Skeleton,
  Thumbnail,
  Tooltip,
  type BreadcrumbItem,
} from '@dacr/library'
import {
  AlertIcon,
  DownloadIcon,
  InfoIcon,
  InfoSmallIcon,
  VocalAndInstrumentalIcon,
  ShareIcon,
  SortIcon,
  TextIcon,
  ThumbsUpIcon,
  EyeOpenIcon,
} from '@dacr/icons'
import { Card } from '@/shared/components/Card'
import { useMutation, useQuery } from '@vue/apollo-composable'
import {
  computed,
  nextTick,
  onActivated,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
  watch,
  type Component,
  type ComponentPublicInstance,
} from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useHead } from '@unhead/vue'
import { useUserStore } from '@/shared/stores/user'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'
import pluralize from 'pluralize'

const device = useDevice()
const route = useRoute()
const router = useRouter()
const userStore = useUserStore()

const breadcrumbItems: BreadcrumbItem[] = [
  { label: 'Infringement Docket' },
  { label: 'Report' },
]

const handleBreadcrumbSelect = (_item: BreadcrumbItem, index: number) => {
  if (index === 0) {
    router.push('/infringement')
  }
}

const {
  result,
  loading: reportLoading,
  fetchMore,
} = useQuery(
  graphql(`
    query Report($id: ID!) {
      report(id: $id) {
        id
        role
        timestamp
        copyright {
          id
          name
          type
          metadata {
            originalFilename
          }
          asset {
            previewImageUrl
          }
          copyright {
            id
          }
          analysis {
            ... on VideoAnalysis {
              duration
            }
          }
        }
        infringingFile {
          id
          name
          type
          copyrightId
          attributes {
            key
            value
          }
          asset {
            previewImageUrl
            ... on DocumentAsset {
              totalPages
              documentUrl
            }
            ... on StreamAsset {
              streamUrl
            }
          }
        }
        audioBundle {
          regularStatus {
            state
            outcome
            reason
            completeCoverage
            catalogueComplete
          }
          coverStatus {
            state
            outcome
            reason
            completeCoverage
            catalogueComplete
          }
          noMatch
          searchedScopeComplete
          catalogueComplete
          acceptedWorksComplete
          totalWindows
          processedWindows
          unprocessedWindows
          acceptedWorks {
            fileId
            userId
            registrationId
            accepted
            matchSources
            coverWitnessesComplete
            regular {
              hashType
              similarityScore
              scoreKind
            }
            coverWitnesses {
              windowIndex
              startMs
              spanMs
              method
              registrationId
              viewId
              score
              gate
              topTied
              admitted
            }
          }
        }
        summary
        score
        level
        matches {
          total
          categories {
            type
            total
            breakdown(input: { first: 20 }) {
              edges {
                cursor
                node {
                  ...MatchBreakdown
                }
              }
              pageInfo {
                endCursor
                hasNextPage
              }
            }
          }
        }
        timeline {
          infringingFile {
            transcript
            audioUrl
            frames
            duration
          }
          copyright {
            transcript
            audioUrl
            frames
            duration
          }
        }
        feedback
        pdf {
          status
          url
        }
        settings {
          isPrivate
        }
        viewers(input: { first: 100, filterBy: ALL }) {
          totalCount
          edges {
            cursor
            role
            node {
              ... on User {
                id
                name
                username
                avatar {
                  url
                }
              }
              ... on Organization {
                id
                name
                avatar {
                  url
                }
                owner {
                  ... on User {
                    id
                    name
                  }
                }
              }
              ... on EmailInvite {
                email
              }
            }
          }
        }
      }
    }
  `),
  {
    id: route.params.id as string,
  },
  {
    clientId: 'router',
  }
)

const { mutate: createFeedback } = useMutation(
  graphql(`
    mutation CreateFeedback($input: CreateFeedbackInput!) {
      createFeedback(input: $input) {
        success
        feedbackId
      }
    }
  `),
  {
    clientId: 'router',
  }
)

const { mutate: removeFeedback } = useMutation(
  graphql(`
    mutation RemoveFeedback($input: RemoveFeedbackInput!) {
      removeFeedback(input: $input) {
        success
      }
    }
  `),
  {
    clientId: 'router',
  }
)

const feedbackState = ref<FeedbackRating | null>(null)

// The feedback mutations echo nothing back, so the cached report keeps the
// rating it was fetched with and a cache-first revisit would show that one.
const cacheFeedback =
  (reportId: string, rating: FeedbackRating | null) =>
  (cache: ApolloCache<unknown>) => {
    cache.modify({
      id: cache.identify({ __typename: 'Report', id: reportId }),
      fields: { feedback: () => rating },
    })
  }

const handleFeedback = (rating: FeedbackRating, message?: string) => {
  if (!report.value || !userStore.user) return

  createFeedback(
    {
      input: {
        rating,
        type: FeedbackType.Report,
        referenceId: report.value.id,
        message,
      },
    },
    { update: cacheFeedback(report.value.id, rating) }
  )
}

const handleRemoveFeedback = () => {
  if (!report.value) return
  feedbackState.value = null
  removeFeedback(
    {
      input: {
        type: FeedbackType.Report,
        referenceId: report.value.id,
      },
    },
    { update: cacheFeedback(report.value.id, null) }
  )
}

const handleThumbsUp = () => {
  if (!canUseOwnerActions.value) return
  if (feedbackState.value === FeedbackRating.Up) {
    handleRemoveFeedback()
    return
  }
  feedbackState.value = FeedbackRating.Up
  handleFeedback(FeedbackRating.Up)
}

const handleThumbsDown = () => {
  if (!canUseOwnerActions.value) return
  if (feedbackState.value === FeedbackRating.Down) {
    handleRemoveFeedback()
    return
  }
  openDialog(Dialog.ReportFeedback, {
    events: {
      success(data?: unknown) {
        feedbackState.value = FeedbackRating.Down
        handleFeedback(FeedbackRating.Down, data as string)
      },
    },
  })
}

const handleShare = () => {
  if (!report.value || !canUseOwnerActions.value) return
  openDialog(Dialog.ShareReport, {
    props: {
      report: report.value,
      getReport: () => report.value,
    },
  })
}

const pdfDownloadUrl = computed(() =>
  report.value?.pdf?.status === 'READY' ? report.value.pdf.url : null
)

const handleDownload = () => {
  const url = pdfDownloadUrl.value
  if (!url) return
  window.open(url, '_blank', 'noopener')
}

const isDesktopLayout = computed(
  () => device.isDesktop.value || device.isTabletHorizontal.value
)

const radius = computed(() => (isDesktopLayout.value ? 85 : 60))

const actualRadius = computed(() =>
  isDesktopLayout.value ? radius.value - 10 : radius.value - 8
)

const circumference = computed(() => actualRadius.value * 2 * Math.PI)

const strokeDashoffset = computed(
  () =>
    circumference.value -
    ((report.value?.score || 0) / 100) * circumference.value
)

const title = computed(() => {
  if (device.isMobile.value || device.isTabletVertical.value) {
    return 'Comparison analysis'
  }

  return 'Technical comparison analysis report'
})

const report = computed(() => result.value?.report)

useHead({
  title: computed(() => {
    const currentReport = report.value
    const fallback = 'Infringement report | Dacr'
    if (!currentReport || currentReport.id !== route.params.id) return fallback

    const createdAt = new Date(currentReport.timestamp)
    if (!Number.isFinite(createdAt.getTime()) || createdAt.getTime() <= 0) {
      return fallback
    }

    const displayId = `DIIS-${createdAt.getUTCFullYear()}-${currentReport.id
      .slice(0, 5)
      .toUpperCase()}`
    return `${displayId} | Infringement report | Dacr`
  }),
})

const isAudioReport = computed(() =>
  ['AUDIO', 'VIDEO'].includes(report.value?.copyright.type ?? '')
)
const coverOnly = computed(
  () =>
    (report.value?.score ?? 0) === 0 &&
    report.value?.audioBundle?.acceptedWorks.some(
      (work) =>
        work.accepted && work.fileId === report.value?.copyright.id && work.matchSources.includes('audio_cover')
    )
)

const reportIdParam = computed(() => route.params.id as string)

const fetchNextCategoryPage = async (
  type: MatchCategoryType,
  after: string
) => {
  await fetchMore({
    query: MATCH_CATEGORY_BREAKDOWN,
    variables: {
      id: reportIdParam.value,
      type,
      first: MATCH_CATEGORY_PAGE_SIZE,
      after,
    },
    updateQuery: (previousData, { fetchMoreResult: newData }) => {
      if (!previousData.report || !newData?.report) return previousData

      const nextCategory = newData.report.matches.categories.find(
        (category) => category.type === type
      )
      const newEdges = nextCategory?.breakdown.edges ?? []

      if (!nextCategory || !newEdges.length) return previousData

      return {
        ...previousData,
        report: {
          ...previousData.report,
          matches: {
            ...previousData.report.matches,
            categories: previousData.report.matches.categories.map(
              (category) =>
                category.type === type
                  ? {
                      ...category,
                      breakdown: {
                        ...nextCategory.breakdown,
                        edges: [...category.breakdown.edges, ...newEdges],
                      },
                    }
                  : category
            ),
          },
        },
      }
    },
  })
}

const categoryMatches: Record<
  MatchCategoryType,
  ReturnType<typeof useMatchCategoryMatches>
> = Object.fromEntries(
  Object.values(MatchCategoryType).map((type) => [
    type,
    useMatchCategoryMatches(
      type,
      computed(() =>
        report.value?.matches.categories.find(
          (category) => category.type === type
        )
      ),
      reportLoading,
      fetchNextCategoryPage
    ),
  ])
) as Record<MatchCategoryType, ReturnType<typeof useMatchCategoryMatches>>

const copyrightPreviewImageUrl = computed(() =>
  report.value?.copyright.asset?.previewImageUrl
    ? applyAuthorizationToken(report.value.copyright.asset.previewImageUrl)
    : undefined
)

const canUseOwnerActions = computed(
  () => report.value?.role === ReportRole.Owner
)

const isPublicReport = computed(() => report.value?.role === ReportRole.Public)

const canPlayReportMedia = computed(() => !isPublicReport.value)

const infringingAsset = computed(() => report.value?.infringingFile.asset)

const infringingStreamUrl = computed(() => {
  const asset = infringingAsset.value
  if (asset?.__typename === 'StreamAsset') return asset.streamUrl
  return undefined
})

const hasInfringingPreviewImage = computed(
  () => !!infringingAsset.value?.previewImageUrl
)

const infringingPreviewImageLoaded = ref(false)
const infringingPreviewImageErrored = ref(false)

const infringingPreviewImageUrl = computed(() =>
  infringingAsset.value?.previewImageUrl
    ? applyAuthorizationToken(infringingAsset.value.previewImageUrl)
    : undefined
)

const showInfringingPreviewImage = computed(
  () =>
    hasInfringingPreviewImage.value &&
    infringingPreviewImageLoaded.value &&
    !infringingPreviewImageErrored.value
)

const infringingPosterUrl = computed(() =>
  showInfringingPreviewImage.value ? infringingPreviewImageUrl.value : undefined
)

const infringingTotalPages = computed(() => {
  const asset = infringingAsset.value
  if (asset?.__typename !== 'DocumentAsset') return null
  return asset.totalPages ?? null
})

watch(
  infringingPreviewImageUrl,
  (url) => {
    infringingPreviewImageLoaded.value = false
    infringingPreviewImageErrored.value = false
    if (!url) return
    const image = new Image()
    image.onload = () => (infringingPreviewImageLoaded.value = true)
    image.onerror = () => (infringingPreviewImageErrored.value = true)
    image.src = url
  },
  { immediate: true }
)

provide(
  'timeline',
  computed(() => report.value?.timeline)
)

const MATCH_TYPE_MAP: Partial<
  Record<MatchCategoryType, TimelineMatch['type']>
> = {
  [MatchCategoryType.Audio]: 'Audio',
  [MatchCategoryType.Video]: 'Video',
  [MatchCategoryType.Transcript]: 'Transcript',
  [MatchCategoryType.Text]: 'Text',
  [MatchCategoryType.Image]: 'Image',
}

const matchScrollId = (breakdown: unknown) => {
  const b = breakdown as Record<string, Record<string, unknown>>
  return String(b.infringingFile?.id ?? '')
}

type CategoryMatches = ReturnType<typeof useMatchCategoryMatches>
type ReportBreakdown = CategoryMatches['edges']['value'][number]['node']

type DisplayMatch = {
  key: string
  type: MatchCategoryType
  match: ReportBreakdown
  number: number
}

type DisplaySection = {
  key: string
  label: string
  icon: Component
  total: number
  matches: DisplayMatch[]
}

const VISUAL_TYPES = [MatchCategoryType.Image, MatchCategoryType.Video]

const sectionMetaFor = (
  type: MatchCategoryType
): { key: string; label: string; icon: Component } => {
  if (VISUAL_TYPES.includes(type)) {
    return { key: 'Visual', label: 'visual', icon: EyeOpenIcon }
  }
  switch (type) {
    case MatchCategoryType.Audio:
      return { key: 'Audio', label: 'audio', icon: VocalAndInstrumentalIcon }
    case MatchCategoryType.Transcript:
      return { key: 'Transcript', label: 'transcript', icon: TextIcon }
    default:
      return { key: 'Text', label: 'text', icon: TextIcon }
  }
}

const displaySections = computed<DisplaySection[]>(() => {
  if (!report.value) return []
  const order: string[] = []
  const sections = new Map<string, DisplaySection>()

  for (const category of report.value.matches.categories) {
    if (!category.total) continue
    const meta = sectionMetaFor(category.type)
    let section = sections.get(meta.key)
    if (!section) {
      section = { ...meta, total: 0, matches: [] }
      sections.set(meta.key, section)
      order.push(meta.key)
    }
    section.total += category.total
    for (const edge of categoryMatches[category.type].edges.value) {
      section.matches.push({
        key: `${category.type}-${edge.cursor}`,
        type: category.type,
        match: edge.node,
        number: section.matches.length + 1,
      })
    }
  }

  return order.map((key) => sections.get(key)!)
})

const matchNumberById = computed(() => {
  const map = new Map<string, number>()
  for (const section of displaySections.value) {
    for (const item of section.matches) {
      map.set(matchScrollId(item.match), item.number)
    }
  }
  return map
})

const timelineMatches = computed<TimelineMatch[]>(() => {
  if (!report.value) return []
  const out: TimelineMatch[] = []

  for (const category of report.value.matches.categories) {
    const type = MATCH_TYPE_MAP[category.type]
    if (!type) continue

    let indexInCategory = 0
    for (const edge of categoryMatches[category.type].edges.value) {
      indexInCategory++
      const b = edge.node as Record<string, Record<string, unknown>>
      const inf = b.infringingFile
      const reg = b.copyright

      const infStart = Number(inf?.start ?? NaN)
      const infEnd = Number(inf?.end ?? NaN)
      const regStart = Number(reg?.start ?? NaN)
      const regEnd = Number(reg?.end ?? NaN)

      if (
        Number.isFinite(infStart) &&
        Number.isFinite(infEnd) &&
        Number.isFinite(regStart) &&
        Number.isFinite(regEnd)
      ) {
        out.push({
          id: String(inf?.id ?? `${type}-${out.length}`),
          type,
          level: (inf?.level as ScoreLevel) ?? ScoreLevel.High,
          title: String(inf?.title ?? type),
          score: Number(inf?.score ?? 0),
          number:
            matchNumberById.value.get(String(inf?.id ?? '')) ?? indexInCategory,
          location: String(inf?.location ?? ''),
          markers: Array.isArray(inf?.markers)
            ? (inf.markers as TimelineMatch['markers'])
            : [],
          infringing: { start: infStart, end: infEnd },
          registered: { start: regStart, end: regEnd },
        })
      }
    }
  }

  return out
})

const timelineData = computed(() => {
  const r = report.value
  if (!r?.timeline) return null

  return {
    infringing: {
      duration: r.timeline.infringingFile.duration,
      audioUrl: r.timeline.infringingFile.audioUrl,
      transcript: r.timeline.infringingFile.transcript ?? undefined,
      frames: r.timeline.infringingFile.frames ?? undefined,
      label: 'Potential infringing work',
      title: r.infringingFile.name,
      icon: {
        type: r.infringingFile.type,
        imageUrl: r.infringingFile.asset.previewImageUrl,
      },
    },
    registered: {
      duration: r.timeline.copyright.duration,
      audioUrl: r.timeline.copyright.audioUrl,
      transcript: r.timeline.copyright.transcript ?? undefined,
      frames: r.timeline.copyright.frames ?? undefined,
      label: 'Registration',
      title: r.copyright.name,
      icon: {
        type: r.copyright.type,
        imageUrl: applyAuthorizationToken(r.copyright.asset?.previewImageUrl),
      },
    },
  }
})

watch(
  report,
  (val) => {
    if (val?.feedback) feedbackState.value = val.feedback
  },
  { immediate: true }
)

const scrollRef = ref<HTMLElement>()
const timelineRef = ref<HTMLElement>()

const mountedMatches = ref(new Set<string>())
const matchElByKey = new Map<string, Element>()
const matchKeyByEl = new Map<Element, string>()
let matchObserver: IntersectionObserver | null = null

const SCROLL_LOAD_MORE_RATIO = 0.8

const handleScroll = () => {
  const el = scrollRef.value
  if (!el) return
  if (
    (el.scrollTop + el.clientHeight) / el.scrollHeight <
    SCROLL_LOAD_MORE_RATIO
  ) {
    return
  }
  for (const category of Object.values(categoryMatches)) {
    if (category.hasMore.value) category.loadMore()
  }
}

const mountMatch = (key: string) => {
  mountedMatches.value.add(key)
  const el = matchElByKey.get(key)
  if (el) {
    matchObserver?.unobserve(el)
    matchKeyByEl.delete(el)
    matchElByKey.delete(key)
  }
}

const ensureMatchObserver = () => {
  if (matchObserver || !scrollRef.value?.isConnected) return
  matchObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue
        const key = matchKeyByEl.get(entry.target)
        if (key) mountMatch(key)
      }
    },
    { root: scrollRef.value, rootMargin: '2000px 0px' }
  )
}

let observeScheduled = false

const scheduleObserve = () => {
  if (observeScheduled) return
  observeScheduled = true
  nextTick(() => {
    observeScheduled = false
    ensureMatchObserver()
    if (!matchObserver) return
    for (const el of matchKeyByEl.keys()) {
      if (el.isConnected) matchObserver.observe(el)
    }
  })
}

onMounted(scheduleObserve)

onActivated(() => {
  matchObserver?.disconnect()
  matchObserver = null
  scheduleObserve()
})

const registerMatchEl = (
  el: Element | ComponentPublicInstance | null,
  key: string
) => {
  if (el instanceof Element) {
    if (mountedMatches.value.has(key)) return
    const prev = matchElByKey.get(key)
    if (prev === el) return
    if (prev) {
      matchObserver?.unobserve(prev)
      matchKeyByEl.delete(prev)
    }
    matchElByKey.set(key, el)
    matchKeyByEl.set(el, key)
    scheduleObserve()
  } else {
    const prev = matchElByKey.get(key)
    if (prev) {
      matchObserver?.unobserve(prev)
      matchKeyByEl.delete(prev)
      matchElByKey.delete(key)
    }
  }
}

const mountSectionsThrough = (sectionKey: string) => {
  for (const section of displaySections.value) {
    for (const item of section.matches) {
      mountMatch(item.key)
    }
    if (section.key === sectionKey) break
  }
}

type BackTarget = 'top' | 'timeline'

const backPill = ref<{
  visible: boolean
  label: string
  target: BackTarget
}>({
  visible: false,
  label: 'Back to top',
  target: 'top',
})

const handleCategoryClick = async (key: string) => {
  mountSectionsThrough(key)
  await nextTick()
  document.querySelector(`#${key}`)?.scrollIntoView({ behavior: 'smooth' })
  backPill.value = {
    visible: true,
    label: 'Back to top',
    target: 'top',
  }
}

const handleTimelineView = async (matchId: string) => {
  const section = displaySections.value.find((s) =>
    s.matches.some((item) => matchScrollId(item.match) === matchId)
  )
  if (section) mountSectionsThrough(section.key)
  await nextTick()
  document
    .getElementById(`match-${matchId}`)
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  backPill.value = {
    visible: true,
    label: 'Back to timeline',
    target: 'timeline',
  }
}

const handleBackPillClick = () => {
  if (backPill.value.target === 'top') {
    scrollRef.value?.scrollTo({ top: 0, behavior: 'smooth' })
  } else {
    timelineRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
  backPill.value = { ...backPill.value, visible: false }
}

const SCROLL_TOP_THRESHOLD = 80

let observer: IntersectionObserver | null = null
let scrollHandler: (() => void) | null = null

const cleanupWatchers = () => {
  observer?.disconnect()
  observer = null
  if (scrollHandler && scrollRef.value) {
    scrollRef.value.removeEventListener('scroll', scrollHandler)
  }
  scrollHandler = null
}

const hidePill = () => {
  backPill.value = { ...backPill.value, visible: false }
  cleanupWatchers()
}

watch(
  () => [backPill.value.visible, backPill.value.target] as const,
  ([visible, target]) => {
    cleanupWatchers()
    if (!visible) return

    if (target === 'top') {
      const el = scrollRef.value
      if (!el) return
      let hasLeft = false
      scrollHandler = () => {
        if (el.scrollTop > SCROLL_TOP_THRESHOLD) {
          hasLeft = true
        } else if (hasLeft) {
          hidePill()
        }
      }
      el.addEventListener('scroll', scrollHandler, { passive: true })
    } else {
      const el = timelineRef.value
      const root = scrollRef.value
      if (!el || !root) return
      let hasLeft = false
      observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry) return
          if (!entry.isIntersecting) {
            hasLeft = true
          } else if (hasLeft) {
            hidePill()
          }
        },
        { root, threshold: 0.2 }
      )
      observer.observe(el)
    }
  },
  { flush: 'post' }
)

onBeforeUnmount(() => {
  cleanupWatchers()
  matchObserver?.disconnect()
  matchObserver = null
  matchElByKey.clear()
  matchKeyByEl.clear()
})
</script>
